import { z } from "zod";
import { HelpdeskConditionPredicate } from "./helpdesk-conditions.ts";
import { PersonRef } from "./people.ts";

export const HelpdeskApprovalInteropSource = z
  .object({
    fieldPath: z.array(z.string().uuid()).min(1).max(10),
    sourceId: z.string().trim().min(1).max(200),
  })
  .strict();
export type HelpdeskApprovalInteropSource = z.infer<typeof HelpdeskApprovalInteropSource>;

const LegacyHelpdeskApprovalInteropConfig = z
  .object({
    notifyStaticApprovers: z.boolean().optional(),
    staticApproverIds: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
    dynamicApproverSources: z.array(HelpdeskApprovalInteropSource).max(100).default([]),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (new Set(value.staticApproverIds.map((id) => id.toLowerCase())).size !== value.staticApproverIds.length)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Static approvers must be unique", path: ["staticApproverIds"] });
    const sourceKeys = value.dynamicApproverSources.map((source) => JSON.stringify([source.fieldPath, source.sourceId]));
    if (new Set(sourceKeys).size !== sourceKeys.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Dynamic approver sources must be unique", path: ["dynamicApproverSources"] });
  });
const AutomaticApprovers = z
  .object({
    kind: z.literal("automatic"),
    staticApproverIds: z.array(z.string().trim().min(1).max(200)).max(100),
    dynamicApproverSources: z.array(HelpdeskApprovalInteropSource).max(100),
  })
  .strict()
  .superRefine((value, ctx) => {
    const parsed = LegacyHelpdeskApprovalInteropConfig.safeParse({ staticApproverIds: value.staticApproverIds, dynamicApproverSources: value.dynamicApproverSources });
    if (!parsed.success) for (const issue of parsed.error.issues) ctx.addIssue(issue);
    if (value.staticApproverIds.length + value.dynamicApproverSources.length === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Automatic approvers require at least one person or source" });
  });

export const HelpdeskApprovalInteropRule = z
  .object({
    condition: HelpdeskConditionPredicate.nullable(),
    approvers: z.union([AutomaticApprovers, z.object({ kind: z.literal("coordinator") }).strict()]),
  })
  .strict();
export type HelpdeskApprovalInteropRule = z.infer<typeof HelpdeskApprovalInteropRule>;

const RulesHelpdeskApprovalInteropConfig = z
  .object({
    mode: z.enum(["first_match", "all_matches"]),
    notifyStaticApprovers: z.boolean().optional(),
    rules: z.array(HelpdeskApprovalInteropRule).max(100),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mode === "all_matches")
      for (const [index, rule] of value.rules.entries()) {
        if (rule.approvers.kind === "coordinator")
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Coordinator selection requires first applicable rule mode", path: ["rules", index, "approvers"] });
      }
  });
export const HelpdeskApprovalInteropConfig = z.union([
  RulesHelpdeskApprovalInteropConfig,
  LegacyHelpdeskApprovalInteropConfig.transform(
    (value): z.infer<typeof RulesHelpdeskApprovalInteropConfig> => ({
      mode: "first_match",
      ...(value.notifyStaticApprovers === undefined ? {} : { notifyStaticApprovers: value.notifyStaticApprovers }),
      rules: [
        {
          condition: null,
          approvers:
            value.staticApproverIds.length + value.dynamicApproverSources.length === 0
              ? { kind: "coordinator" }
              : { kind: "automatic", staticApproverIds: value.staticApproverIds, dynamicApproverSources: value.dynamicApproverSources },
        },
      ],
    }),
  ),
]);
export type HelpdeskApprovalInteropConfig = z.infer<typeof HelpdeskApprovalInteropConfig>;

export const HelpdeskApprovalInteropProvenance = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("static") }).strict(),
  HelpdeskApprovalInteropSource.extend({
    kind: z.literal("field"),
    groupPath: z.array(z.object({ groupId: z.string().uuid(), itemIndex: z.number().int().nonnegative() }).strict()).optional(),
  }),
]);
export type HelpdeskApprovalInteropProvenance = z.infer<typeof HelpdeskApprovalInteropProvenance>;

export const HelpdeskApprovalInteropIntent = z
  .object({
    version: z.literal(1),
    capturedAt: z.string().datetime({ offset: true }),
    notifyStaticApprovers: z.boolean().optional(),
    approvers: z.array(z.object({ person: PersonRef, sources: z.array(HelpdeskApprovalInteropProvenance).min(1) }).strict()).max(1000),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (new Set(value.approvers.map((approver) => approver.person.entraId.toLowerCase())).size !== value.approvers.length)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Approvers must be unique", path: ["approvers"] });
  });
export type HelpdeskApprovalInteropIntent = z.infer<typeof HelpdeskApprovalInteropIntent>;
